import React, { useContext, useEffect, useState, useRef } from "react";
import axios from "axios";
import RegistrationContext from "../contexts/RegistrationContext";
import { base_url } from "../config";
import WhiteBoxLayout from "../components/WhiteBoxLayout/WhiteBoxLayout";
import { Info, TriangleAlert, Trash2 } from "lucide-react";
import "./style.css";

function Membership() {
  const { reload, setReload, clickedNext, setError, setCart, registrationType } =
    useContext(RegistrationContext);

  const isStudent =
    registrationType === "student" || registrationType === "student-paper";

  const [status, setStatus] = useState({
    is_ijcai_member: false,
    ijcai_member_until: null,
    is_ppi_recipient: false,
    ppi_tier: null,
    ppi_discount: null,
    claims_eurai_member: false,
    eurai_member_validated: false,
    eurai_proof_file_name: null,
    purchase_ijcai_membership: null,
  });
  const [purchase, setPurchase] = useState(null);

  const submitBtn = useRef();

  useEffect(() => {
    axios
      .get(`${base_url}/api/membership_status`)
      .then((r) => {
        setStatus(r.data);
        setPurchase(r.data.purchase_ijcai_membership ?? null);
      })
      .catch(() => {});
  }, []);

  const handleSelect = async () => {
    try {
      await axios.post(`${base_url}/api/progress`, {
        purchase_ijcai_membership: purchase,
      });
      setReload(reload + 1);
    } catch (e) {
      setError &&
        setError("Unexpected error. " + (e.response?.data?.message || ""));
    }
  };

  useEffect(() => {
    if (clickedNext === 0) return;
    submitBtn.current && submitBtn.current.click();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clickedNext]);

  const onProofChange = async (e) => {
    if (!e.target.files?.[0]) return;
    const f = e.target.files[0];
    if (f.size > 5 * 1024 * 1024) {
      setError && setError("File exceeds 5MB.");
      return;
    }
    if (f.type !== "application/pdf") {
      setError && setError("Only PDF files are accepted.");
      return;
    }
    const form = new FormData();
    form.append("file", f);
    try {
      const up = await axios.post(`${base_url}/api/update_eurai_proof`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (up.data?.cart) setCart(up.data.cart);
      const r = await axios.get(`${base_url}/api/membership_status`);
      setStatus(r.data);
    } catch (err) {
      setError && setError("Upload failed.");
    }
  };

  const removeProof = async () => {
    try {
      const up = await axios.post(`${base_url}/api/remove_eurai_proof`, {});
      if (up.data?.cart) setCart(up.data.cart);
      const r = await axios.get(`${base_url}/api/membership_status`);
      setStatus(r.data);
    } catch {
      setError && setError("Could not remove proof.");
    }
  };

  const showEuraiSection = !status.is_ppi_recipient;
  const euraiDisabled = status.is_ijcai_member;
  const showPurchaseSection = !status.is_ijcai_member;
  const membershipPrice = isStudent ? 50 : 100;

  return (
    <WhiteBoxLayout title="Membership" handleSelect={handleSelect}>
      <div className="w-full max-w-6xl mx-auto bg-white p-4 rounded-lg">
        <h1 className="text-xl font-bold mb-4">Membership</h1>

        <section className="mb-6 space-y-3 text-sm">
          <p>
            IJCAI and EurAI members are entitled to a USD 50 discount on the
            main conference registration. Discounts do not stack.
          </p>
        </section>

        {/* Auto-detected status banners */}
        <div className="space-y-3 mb-6">
          {status.is_ijcai_member && (
            <div className="rounded-lg border border-green-300 bg-green-50 p-3 flex items-start gap-2">
              <Info className="h-5 w-5 text-green-600 mt-0.5 shrink-0" />
              <div className="text-sm">
                <b>You are an IJCAI member.</b> A USD 50 discount applies to
                your main conference fee.
                {status.ijcai_member_until && (
                  <> Membership valid until Sept 1st, 2028.</>
                )}
                
              </div>
            </div>
          )}

          {status.is_ppi_recipient && (
            <div className="rounded-lg border border-purple-300 bg-purple-50 p-3 flex items-start gap-2">
              <Info className="h-5 w-5 text-purple-600 mt-0.5 shrink-0" />
              <div className="text-sm">
                <b>
                  PPI award detected
                  {status.ppi_tier ? ` (${status.ppi_tier} tier)` : ""}.
                </b>{" "}
                A USD {status.ppi_discount} discount has been applied to your
                main conference fee.
              </div>
            </div>
          )}
        </div>

        {/* Purchase membership */}
        {showPurchaseSection && (
          <section className="mb-6 border rounded-lg p-4">
            <h2 className="font-semibold mb-2">IJCAI membership</h2>
            <p className="text-sm text-gray-600 mb-3">
              Your email is not registered as belonging to an active IJCAI member.
            </p>
            <p className="text-sm text-gray-600 mb-3">
              3-year membership: Sept 1, 2026 — Sept 1, 2029. Entitles you to
              discounts on future conference registrations from 2026 onward.
            </p>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={!!purchase}
                onChange={async (e) => {
                  const next = e.target.checked
                    ? (isStudent ? "student" : "regular")
                    : null;
                  setPurchase(next);
                  try {
                    const r = await axios.post(
                      `${base_url}/api/update_membership_selection`,
                      { purchase_ijcai_membership: next },
                    );
                    if (r.data?.cart) setCart(r.data.cart);
                  } catch (err) {
                    setError &&
                      setError("Could not update membership selection.");
                  }
                }}
                className="h-4 w-4"
              />
              Join IJCAI for 3 years — USD {membershipPrice}
              {isStudent ? " (student rate)" : ""}
            </label>
          </section>
        )}

        {/* EurAI section */}
        {showEuraiSection && (
          <section
            className={`mb-6 border rounded-lg p-4 ${
              euraiDisabled ? "opacity-60 pointer-events-none bg-gray-50" : ""
            }`}
          >
            <h2 className="font-semibold mb-2">EurAI membership</h2>
            {euraiDisabled ? (
              <p className="text-sm text-gray-700 mb-3">
                You are a confirmed IJCAI member — the USD 50 member discount
                has already been applied. No EurAI proof is required.
              </p>
            ) : (
              <p className="text-sm text-gray-600 mb-3">
                If you are a EurAI member, upload a PDF proof of membership
                and your USD 50 discount will be applied immediately. Your
                registration becomes valid once an admin verifies the proof.
              </p>
            )}

            {!euraiDisabled && (status.eurai_proof_file_name ? (
              <div className="rounded-md border bg-blue-50 border-blue-300 p-3 flex items-center gap-2 text-sm">
                <Info className="h-5 w-5 text-blue-600 shrink-0" />
                <span className="flex-1">
                  <b>{status.eurai_proof_file_name}</b>{" "}
                  {status.eurai_member_validated
                    ? "— validated ✓"
                    : "— pending validation"}
                </span>
                <button
                  type="button"
                  onClick={removeProof}
                  className="text-red-600 hover:text-red-700"
                  aria-label="Remove proof"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ) : (
              <div className="rounded-md border border-amber-300 bg-amber-50 p-3 space-y-2">
                <div className="flex items-start gap-2 text-sm">
                  <TriangleAlert className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
                  <span>
                    Upload your EurAI membership proof (PDF, max 5MB).
                  </span>
                </div>
                <input
                  type="file"
                  accept=".pdf"
                  onChange={onProofChange}
                  className="block w-full text-sm text-gray-700 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-amber-200 file:text-amber-800 hover:file:bg-amber-300"
                />
              </div>
            ))}
          </section>
        )}

  

        <button
          type="button"
          ref={submitBtn}
          onClick={handleSelect}
          className="hidden"
        >
          hidden
        </button>
      </div>
    </WhiteBoxLayout>
  );
}

export default Membership;
